import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Conditions d\'Utilisation - Agent IA Pro | CGU Agents IA';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('robots', 'noindex, nofollow');
    setMeta('description', 'Conditions générales d\'utilisation d\'Agent IA Pro. Règles d\'utilisation de la plateforme d\'agents IA, abonnements, paiements et responsabilités.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terms-of-service`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terms-of-service`);
      document.head.appendChild(canonical);
    }

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(s => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Conditions d'Utilisation - Agent IA Pro",
      "description": "Conditions générales d'utilisation de la plateforme Agent IA Pro.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terms-of-service`,
      "publisher": {
        "@type": "Organization",
        "name": "Agent IA Pro",
        "url": import.meta.env.VITE_SITE_URL || 'https://example.com'
      }
    });
    document.head.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Conditions d'Utilisation
          </h1>
          
          <p className="text-gray-600 mb-12">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                1. Acceptation des conditions
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                En accédant et en utilisant notre plateforme d'agents IA, vous acceptez d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Ces conditions constituent un accord juridiquement contraignant entre vous et notre entreprise. Nous nous réservons le droit de modifier ces conditions à tout moment, et votre utilisation continue de la plateforme après de telles modifications constitue votre acceptation des nouvelles conditions.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                2. Description des services
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Notre plateforme fournit des services d'agents IA intelligents conçus pour automatiser et optimiser diverses tâches professionnelles. Nos services incluent :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Accès à différents types d'agents IA spécialisés</li>
                <li>Outils de personnalisation et de configuration des agents</li>
                <li>Tableaux de bord analytiques et rapports de performance</li>
                <li>Support technique et assistance client</li>
                <li>Mises à jour régulières et nouvelles fonctionnalités</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                3. Inscription et compte utilisateur
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    3.1 Création de compte
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Pour utiliser nos services, vous devez créer un compte en fournissant des informations exactes, complètes et à jour. Vous êtes responsable de maintenir la confidentialité de vos identifiants de connexion et de toutes les activités effectuées sous votre compte.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    3.2 Éligibilité
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Vous devez avoir au moins 18 ans pour utiliser nos services. En créant un compte, vous déclarez et garantissez que vous avez l'âge légal requis et la capacité juridique pour conclure cet accord.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    3.3 Sécurité du compte
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Vous devez immédiatement nous informer de toute utilisation non autorisée de votre compte ou de toute autre violation de sécurité. Nous ne serons pas responsables des pertes résultant d'une utilisation non autorisée de votre compte.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                4. Utilisation acceptable
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vous acceptez d'utiliser nos services uniquement à des fins légales et conformément à ces conditions. Vous vous engagez à ne pas :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Violer toute loi ou réglementation applicable</li>
                <li>Porter atteinte aux droits de propriété intellectuelle d'autrui</li>
                <li>Transmettre du contenu illégal, offensant ou nuisible</li>
                <li>Tenter d'accéder de manière non autorisée à nos systèmes</li>
                <li>Utiliser nos services pour créer, distribuer ou faciliter des logiciels malveillants</li>
                <li>Harceler, menacer ou intimider d'autres utilisateurs</li>
                <li>Utiliser nos agents IA pour générer du contenu trompeur ou frauduleux</li>
                <li>Revendre ou redistribuer nos services sans autorisation</li>
                <li>Effectuer du reverse engineering ou tenter de copier notre technologie</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                5. Tarification et paiement
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    5.1 Abonnements
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nos services sont proposés selon différents plans d'abonnement. Les frais d'abonnement sont facturés à l'avance sur une base mensuelle ou annuelle, selon le plan choisi. Tous les paiements sont non remboursables, sauf indication contraire dans notre politique de remboursement.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    5.2 Renouvellement automatique
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Votre abonnement se renouvellera automatiquement à la fin de chaque période de facturation, sauf si vous l'annulez avant la date de renouvellement. Nous vous enverrons un rappel avant chaque renouvellement.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    5.3 Modifications tarifaires
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nous nous réservons le droit de modifier nos tarifs à tout moment. Toute modification de prix sera communiquée avec un préavis de 30 jours et prendra effet lors de votre prochain cycle de facturation.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    5.4 Taxes
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Les prix affichés n'incluent pas les taxes applicables. Vous êtes responsable du paiement de toutes les taxes, droits et autres charges gouvernementales associés à votre utilisation de nos services.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                6. Propriété intellectuelle
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tous les droits de propriété intellectuelle relatifs à notre plateforme, y compris mais sans s'y limiter, les logiciels, le contenu, les marques, les logos et les designs, sont notre propriété exclusive ou celle de nos concédants de licence.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vous conservez tous les droits sur le contenu que vous créez ou téléchargez sur notre plateforme. En utilisant nos services, vous nous accordez une licence limitée pour utiliser, stocker et traiter votre contenu uniquement dans le but de fournir nos services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Le contenu généré par nos agents IA vous appartient, sous réserve du respect de ces conditions et des lois applicables sur la propriété intellectuelle.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                7. Confidentialité et protection des données
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Votre vie privée est importante pour nous. Notre utilisation de vos données personnelles est régie par notre <a href="/privacy-policy" className="text-teal-600 hover:text-teal-700 font-medium">Politique de Confidentialité</a>, qui fait partie intégrante de ces conditions d'utilisation. Nous vous encourageons à la lire attentivement.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                8. Garanties et limitations
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    8.1 Disponibilité du service
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nous nous efforçons de maintenir nos services disponibles 24h/24 et 7j/7, mais nous ne garantissons pas une disponibilité ininterrompue. Nous pouvons suspendre temporairement l'accès pour maintenance, mises à jour ou raisons techniques.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    8.2 Limitation de garantie
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nos services sont fournis "en l'état" et "selon disponibilité". Nous ne garantissons pas que nos services répondront à vos besoins spécifiques ou que les résultats générés par nos agents IA seront toujours exacts ou fiables.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    8.3 Limitation de responsabilité
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Dans toute la mesure permise par la loi, nous ne serons pas responsables des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs, y compris la perte de profits, de données ou d'opportunités commerciales.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                9. Résiliation
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    9.1 Résiliation par vous
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Vous pouvez résilier votre compte à tout moment via les paramètres de votre compte ou en nous contactant. La résiliation prendra effet à la fin de votre période de facturation en cours.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    9.2 Résiliation par nous
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Nous nous réservons le droit de suspendre ou de résilier votre compte immédiatement si vous violez ces conditions, si votre utilisation met en danger nos systèmes ou d'autres utilisateurs, ou pour toute autre raison légitime.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    9.3 Effets de la résiliation
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Après la résiliation, vous perdrez l'accès à votre compte et à tout contenu associé. Nous pouvons supprimer vos données conformément à notre politique de conservation des données.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                10. Indemnisation
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Vous acceptez de nous indemniser, de nous défendre et de nous dégager de toute responsabilité contre toute réclamation, perte, dommage, responsabilité et dépense (y compris les frais d'avocat raisonnables) découlant de votre utilisation de nos services, de votre violation de ces conditions ou de votre violation des droits d'un tiers.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                11. Droit applicable et juridiction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ces conditions sont régies par les lois françaises, sans égard aux principes de conflits de lois. Tout litige découlant de ces conditions sera soumis à la juridiction exclusive des tribunaux de Paris, France.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Si une disposition de ces conditions est jugée invalide ou inapplicable, les autres dispositions resteront pleinement en vigueur.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                12. Modifications des conditions
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Nous nous réservons le droit de modifier ces conditions à tout moment. Nous vous informerons de tout changement important par e-mail ou via une notification sur notre plateforme. Votre utilisation continue de nos services après de telles modifications constitue votre acceptation des nouvelles conditions.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                13. Dispositions générales
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Intégralité de l'accord :</strong> Ces conditions constituent l'intégralité de l'accord entre vous et nous concernant l'utilisation de nos services</li>
                <li><strong>Renonciation :</strong> Notre non-exercice d'un droit ne constitue pas une renonciation à ce droit</li>
                <li><strong>Cession :</strong> Vous ne pouvez pas céder ces conditions sans notre consentement écrit préalable</li>
                <li><strong>Force majeure :</strong> Nous ne serons pas responsables des retards ou défaillances dus à des circonstances indépendantes de notre volonté</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                14. Contact
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Pour toute question concernant ces conditions d'utilisation, vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Email :</strong> <a href="mailto:legal@example.com" className="text-teal-600 hover:text-teal-700">legal@example.com</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Téléphone :</strong> +33 1 23 45 67 89
                </p>
                <p className="text-gray-700">
                  <strong>Adresse :</strong> 123 Avenue des Champs-Élysées, 75008 Paris, France
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
